import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Comercio internacional";
const heroImage = "https://net-basis.net/Bilder/international-trade-dach-local-expertise.webp";
const heroAlt = "Un empresario señala un mapa mundial conectado para el comercio internacional en el mercado DACH.";
const heroClaimLine1 = "¡Superar fronteras!";
const heroClaimLine2 = "¡Con experiencia local!";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __comercio internacional__:";
const h2Intro = "NetBasis International es el socio comercial conectado y de confianza para sus clientes en nuestros mercados. Acompañamos a nuestros fabricantes socios extranjeros en el inicio de nuevas actividades comerciales con empresas locales y socios o clientes sobre el terreno.";
const p1Text = "Realizamos los análisis de mercado y comercio necesarios para aportar los conocimientos indispensables frente a la competencia. NetBasis International apoya a sus fabricantes socios en la evaluación del potencial comercial y de clientes, la valoración de posibles alianzas, así como en cuestiones relativas al control de calidad, gestión, estilo de liderazgo y finanzas.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Identificación de las estructuras de precio más efectivas, así como de las condiciones de pago y garantía óptimas mediante el apoyo en negociaciones contractuales",
  "Validación jurídica y cierre seguro de nuevos proyectos comerciales en colaboración con la dirección, los departamentos jurídicos o bufetes de abogados locales",
  "Perspectivas valiosas y actualizaciones continuas sobre las prácticas comerciales locales y normativas comerciales",
  "Apoyo integral para la obtención de las licencias comerciales necesarias y acuerdos comerciales estratégicos",
  "Respuestas profesionales y aclaración de todas las consultas relacionadas con los procedimientos de comercio internacional",
  "Procesamiento de solicitudes de procedimientos de importación, declaraciones de aduana y documentación asociada",
  "Garantía de plazos de entrega fiables y programaciones estables mediante el apoyo específico a la cadena de suministro",
  "Aseguramiento del cumplimiento total de envíos y expediciones con respecto a las normas y reglamentos nacionales e internacionales",
];

export default function ComercioInternacionalEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Comercio Internacional | NetBasis International"
        description="Su nexo competente para las estructuras comerciales globales y las relaciones comerciales B2B exitosas en el sector TIC."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/comercio-internacional"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center pt-[28%] md:pt-[11%] lg:pt-[14%] pointer-events-none">
                <p
                  className="font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[globusTextSlide_12s_ease-in-out_infinite] max-w-[85%] sm:max-w-[80%] text-[1.65rem] sm:text-3xl md:text-4xl lg:text-5xl"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  <span className="block">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h2Intro}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}